import { useComboBox } from '@react-aria/combobox';
import { Item } from '@react-stately/collections';
import { useComboBoxState } from '@react-stately/combobox';
import type { AriaComboBoxProps } from '@react-types/combobox';
import LoadingPanel from 'bz-core-controls/controls/layout/loading-panel';
import classNames from 'classnames';
import { Popover } from 'click-controls/controls/popover';
import { debounce, extend } from 'lodash';
import type { ComponentClass } from 'react';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import Api from 'tm3-core/api';
import ListBox from '../../../listbox';

export interface IProps extends AriaComboBoxProps<any> {
  for: string;
  label: string;
  src: string;
  arguments: IArgument;
  onChange: any;
  liveOnChange: boolean;
  itemTemplate: ComponentClass;
  placeholder: string;
  showLoadMore: boolean;
  containerClass?: string;
  inputClass?: string;
  labelClass?: string;
  required?: string;
  errorText?: string;
  submitted?: boolean;
  errorTextClassName?: string;
}
interface IComboboxProps extends IProps {
  isMenuOpen: boolean;
  isLoading: boolean;
  loadMore: ILoadmore;
  isResultFound: boolean;
}
interface ILoadmore {
  count: number;
  getMoreClients: () => void;
}
interface IArgument {
  startIdx?: number;
  endIdx?: number;
  filterId?: number;
  realOnly?: boolean;
}

const ComboBoxAria: React.FC<IComboboxProps> = (props) => {
  let state = useComboBoxState(props);

  let inputRef = React.useRef(null);
  let listBoxRef = React.useRef(null);
  let popoverRef = React.useRef(null);

  let { inputProps, listBoxProps, labelProps } = useComboBox(
    {
      ...props,
      inputRef,
      listBoxRef,
      popoverRef
    },
    state
  );

  useEffect(() => {
    if (props.isMenuOpen) {
      state.open(null);
    }
  }, [props.isMenuOpen]);

  const onLoadmore = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    props.loadMore.getMoreClients();
  };
  let errorArray = !!props?.errorText ? props.errorText.split(',') : [];

  return (
    <div className={classNames('relative', props.containerClass)}>
      <label {...labelProps} className={classNames('click-label', props.labelClass)}>
        <span>
          {props.label}
          {props.required && <span className="text-normal pl-px text-rose-600">*</span>}
        </span>
      </label>
      <div className="relative">
        <input {...inputProps} ref={inputRef} className="click-input" />
        {props.isLoading && !props.isMenuOpen && (
          <div className="absolute right-9 top-3">
            {' '}
            <LoadingPanel loading={props.isLoading} size={0.5} />
          </div>
        )}
        {!props.isResultFound && !props.isLoading && state.inputValue !== '' && (
          <p
            style={{ marginTop: '-11px' }}
            className="absolute right-0 text-xs font-medium text-rose-600"
          >
            No result found
          </p>
        )}
        {!!props.errorText && props.submitted && state.inputValue == '' && (
          <div
            style={{ marginTop: '-11px' }}
            className={`absolute right-0 text-xs font-medium text-rose-600 ${props.errorTextClassName}`}
          >
            <p>{errorArray.length && errorArray[errorArray.length - 1]}</p>
          </div>
        )}
        {state.isOpen && (
          <Popover
            popoverRef={popoverRef}
            isOpen={state.isOpen}
            onClose={state.close}
            className="absolute top-full z-10  mt-2 w-full rounded-md border border-gray-300 bg-white shadow-lg"
          >
            <ListBox {...listBoxProps} listBoxRef={listBoxRef} state={state} />
            {props.loadMore.count >= 25 && (
              <div className="flex justify-center">
                {' '}
                <button
                  className="btn m-1 flex w-full justify-center rounded-md px-2 py-2 text-sm outline-none"
                  onClick={onLoadmore}
                >
                  Load more
                </button>
              </div>
            )}
          </Popover>
        )}
      </div>
    </div>
  );
};

export default function FieldAutoSuggest(props: IProps) {
  let [fieldState, setFieldState] = useState({
    selectedKey: '',
    inputValue: '',
    term: '',
    args: {
      startIdx: 0,
      endIdx: 0
    },
    index: 0,
    loading: false,
    isOpen: false,
    filtered: (typeof props.src !== 'string' ? props.src : null) || [],
    count: 0,
    resultFound: false
  });

  let onSelectionChange = (key: any) => {
    const selectedItem = fieldState.filtered.find((element) => element.id === key);
    setFieldState((prevState) => {
      return {
        ...prevState,
        isOpen: false,
        filtered: []
      };
    });
    if (!!selectedItem) props.onChange(selectedItem);
  };
  let onInputChange = (value: string) => {
    setFieldState((prevState) => {
      return {
        ...prevState,
        inputValue: value,
        selectedKey: value === '' ? '' : prevState.selectedKey,
        filtered: [],
        loading: true,
        term: value,
        args: extend({}, { term: value }, props.arguments)
      };
    });
  };

  const onSearch = () => {
    if (props.src.match(/^\/api\//)) {
      let filtered = JSON.parse(JSON.stringify(fieldState.filtered));
      Api.post(props.src, fieldState.args).then((res) => {
        filtered.push(...res.body.results);
        setFieldState((prevState) => {
          return {
            ...prevState,
            filtered: filtered,
            count: res.body.total,
            loading: false,
            isOpen: true
          };
        });
      });
    }
  };
  const searchDebounced = useCallback(
    debounce((fieldState) => {
      if (props.src.match(/^\/api\//)) {
        let filtered = JSON.parse(JSON.stringify(fieldState.filtered));
        Api.post(props.src, fieldState.args).then((res) => {
          if (res.body.total === 0) {
            setFieldState((prev) => {
              return {
                ...prev,
                filtered: filtered,
                count: res.body.total,
                loading: false,
                isOpen: true,
                resultFound: false
              };
            });
          } else {
            filtered.push(...res.body.results);
            setFieldState((prevState) => {
              return {
                ...prevState,
                filtered: filtered,
                count: res.body.total,
                loading: false,
                isOpen: true,
                resultFound: true
              };
            });
          }
        });
      }
    }, 1000),
    []
  );

  const loadMore = {
    count: fieldState.count,
    getMoreClients: () => {
      let args = fieldState.args;
      args.startIdx += args.endIdx;
      args.endIdx += 25;
      setFieldState((prevState) => {
        return {
          ...prevState,
          args: args,
          loading: true
        };
      });
      onSearch();
    }
  };

  useEffect(() => {
    if (fieldState.term !== '') {
      setFieldState((prevState) => {
        return {
          ...prevState,
          isOpen: false,
          loading: true
        };
      });
      searchDebounced(fieldState);
    } else {
      setFieldState((prevState) => {
        return {
          ...prevState,
          isOpen: false,
          loading: false
        };
      });
    }
  }, [fieldState.term]);

  useEffect(() => {
    if (fieldState.filtered.length > 0 && fieldState.term !== '') {
      setFieldState((prevState) => {
        return {
          ...prevState,
          isOpen: true
        };
      });
    }
  }, [fieldState.filtered]);

  return (
    <div>
      <ComboBoxAria
        {...props}
        items={fieldState.filtered}
        selectedKey={fieldState.selectedKey}
        inputValue={fieldState.inputValue}
        onSelectionChange={onSelectionChange}
        onInputChange={onInputChange}
        isMenuOpen={fieldState.isOpen}
        isLoading={fieldState.loading}
        loadMore={loadMore}
        isResultFound={fieldState.resultFound}
      >
        {(item: any) => (
          <Item>
            {React.createElement(
              props.itemTemplate,
              typeof item == 'string' ? { text: item } : item
            )}
          </Item>
        )}
      </ComboBoxAria>
    </div>
  );
}
